import { computed, inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import handlePurchase from '@/services/modules/purchase'
import handleCompany from '@/services/modules/company'
import registerPrinter from '@/services/utils/pdf/purchaseRegister'

export default function usePurchaseRegisterTab(tab, getEmptyFilters, getFilterQuery) {
    const $router = useRouter()
    const $route = useRoute()
    const $store = useStore()
    const showError = inject('showError')
    const { fetchPurchaseRegister } = handlePurchase()
    const { fetchCompanyInfo } = handleCompany()
    const { exportToPDF } = registerPrinter()

    const offset = 20
    const loading = ref(false)
    const hasLoaded = ref(false)
    const rows = ref([])
    const registerData = ref({})
    const companyInfo = ref({})
    const toolbarRef = ref(null)
    const appliedFilters = ref(getEmptyFilters())
    const draftFilters = ref(getEmptyFilters())

    const companyId = computed(() => $route.params.companyId)
    const start = computed(() => $route.query.start)
    const end = computed(() => $route.query.end)
    const token = computed(() => localStorage.getItem('token'))

    const pageData = computed(() => {
        if (! registerData.value.data) return {}
        return registerData.value.data
    })

    const cloneFilters = (filters) => {
        return Object.assign({}, filters)
    }

    const getBaseQuery = () => {
        let query = `?company_id=${companyId.value}&tab=${tab}`
        if (start.value) query += '&start_date=' + start.value
        if (end.value) query += '&end_date=' + end.value
        return query + getFilterQuery(appliedFilters.value)
    }

    const getPageQuery = () => {
        let query = getBaseQuery() + '&offset=' + offset
        if ($route.query.page) query += '&page=' + $route.query.page
        return query
    }

    const setPagination = (data) => {
        $store.commit('setPaginationData', {
            records: data.total,
            perPage: data.per_page,
            currentPage: data.current_page
        })
    }

    const resetTable = () => {
        rows.value = []
        registerData.value = {}
        $store.commit('resetPagination')
    }

    const fetchRegister = async () => {
        loading.value = true
        try {
            const res = await fetchPurchaseRegister(getPageQuery())
            if (! res.status) {
                resetTable()
                showError(res.message)
                return
            }
            rows.value = res.data.data
            registerData.value = res
            setPagination(res.data)
        } catch (err) {
            showError('Something went wrong')
        } finally {
            hasLoaded.value = true
            loading.value = false
        }
    }

    const resetPage = async () => {
        $store.commit('resetPagination')
        await $router.push({ path: $route.path, query: Object.assign({}, $route.query, { page: 1 }) })
    }

    const onClickGo = async () => {
        await resetPage()
        await fetchRegister()
    }

    const onPageChange = async (page) => {
        const routeQuery = Object.assign({}, $route.query)
        routeQuery.page = page
        await $router.push({ path: $route.path, query: routeQuery })
        await fetchRegister()
    }

    const onOpenFilter = () => {
        draftFilters.value = cloneFilters(appliedFilters.value)
    }

    const onApplyFilters = async () => {
        appliedFilters.value = cloneFilters(draftFilters.value)
        await onClickGo()
    }

    const onClearFilters = async () => {
        appliedFilters.value = getEmptyFilters()
        draftFilters.value = getEmptyFilters()
        await onClickGo()
    }

    const onRemoveChip = async (key) => {
        appliedFilters.value[key] = getEmptyFilters()[key]
        await onClickGo()
    }

    const onOpenContactSearchModal = () => {
        $store.state.isModalOpenThree = true
    }

    const onSearchContact = (contact) => {
        const draft = Object.assign({}, draftFilters.value, { supplier: contact })
        if (toolbarRef.value) toolbarRef.value.openFilter()
        draftFilters.value = draft
    }

    const hasDateRange = () => {
        if (start.value && end.value) return true
        showError('Please select a date range')
        return false
    }

    const onClickToPrint = async () => {
        if (! hasDateRange()) return
        loading.value = true
        try {
            const res = await fetchPurchaseRegister(getBaseQuery() + '&is_all=1')
            if (! res.status) {
                showError(res.message)
                return
            }
            if (! res.data.data.length) {
                showError('No data found')
                return
            }
            if (res.data.total > res.data.per_page) {
                showError(`Too many rows to print (${res.data.total}). Narrow the filters or use Export`)
                return
            }
            exportToPDF(companyInfo.value, res, tab, start.value, end.value)
        } catch (err) {
            showError('Something went wrong')
        } finally {
            loading.value = false
        }
    }

    const downloadExport = (path) => {
        if (! hasDateRange()) return
        const url = `${process.env.VUE_APP_BASE_URL}${path}${getBaseQuery()}&_token=${token.value}`
        const anchor = document.createElement('a')
        anchor.setAttribute('href', url)
        anchor.click()
    }

    const exportTable = () => {
        downloadExport('/export/inventory/purchase-register')
    }

    const exportInputTaxStatement = () => {
        downloadExport('/export/inventory/input-tax-statement')
    }

    const setCompanyInfo = async () => {
        try {
            const res = await fetchCompanyInfo(companyId.value)
            companyInfo.value = res.data
        } catch (err) {
            showError('Failed to load company info')
        }
    }

    onMounted(() => {
        setCompanyInfo()
    })

    return {
        offset,
        loading,
        hasLoaded,
        rows,
        registerData,
        pageData,
        toolbarRef,
        appliedFilters,
        draftFilters,
        onClickGo,
        onPageChange,
        onOpenFilter,
        onApplyFilters,
        onClearFilters,
        onRemoveChip,
        onOpenContactSearchModal,
        onSearchContact,
        onClickToPrint,
        exportTable,
        exportInputTaxStatement
    }
}
